/**
 * Who the player is. Chosen on the landing screen before the run and read back
 * by the dialogue and the end screens.
 *
 * Deliberately free of DOM and Three: the landing writes it, but a harness that
 * skips the landing entirely (any query string — see `shouldAutoBoot` in
 * main.ts) still boots with a valid identity, so the defaults live here rather
 * than in the control that collects them.
 */

export type PlayerVoice = 'male' | 'female' | 'neutral';

export interface PlayerIdentity {
  readonly name: string;
  readonly voice: PlayerVoice;
}

const STORAGE_KEY = 'extinctrum.identity';

export const DEFAULT_NAME = 'Survivor';
export const DEFAULT_VOICE: PlayerVoice = 'neutral';

/** Long enough for a real name, short enough not to break the end-screen copy. */
export const MAX_NAME = 24;

const VOICES: PlayerVoice[] = ['male', 'female', 'neutral'];

export function isVoice(v: unknown): v is PlayerVoice {
  return typeof v === 'string' && (VOICES as string[]).includes(v);
}

/**
 * Trim, collapse runs of whitespace, drop control characters, cap the length.
 * An empty or all-whitespace name is the default — the run never blocks on it.
 */
export function sanitizeName(raw: string): string {
  const cleaned = raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_NAME);
  return cleaned.length > 0 ? cleaned : DEFAULT_NAME;
}

export function defaultIdentity(): PlayerIdentity {
  return { name: DEFAULT_NAME, voice: DEFAULT_VOICE };
}

export function readIdentity(): PlayerIdentity {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultIdentity();
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return defaultIdentity();
    const { name, voice } = parsed as { name?: unknown; voice?: unknown };
    return {
      name: typeof name === 'string' ? sanitizeName(name) : DEFAULT_NAME,
      voice: isVoice(voice) ? voice : DEFAULT_VOICE,
    };
  } catch {
    // Private mode, or a hand-mangled value — either way, play anyway.
    return defaultIdentity();
  }
}

export function writeIdentity(id: PlayerIdentity): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(id));
  } catch {
    /* private mode — ignore */
  }
}
